import { useCallback, useEffect, useRef, useState } from "react";

import {
  NoteFieldRow,
  noteDraft,
  noteFieldsFor,
  noteUpdateBody,
} from "@/components/NoteFieldRow";
import { TagChips } from "@/components/TagChips";
import { isTextEntry } from "@/lib/dom";
import { forgetSuggestedTags } from "@/lib/tags";
import { attachMedia, clearMedia, getNote, updateNote, type NoteDetail } from "@/lib/note";

import { ImageSlots, type SlotKey } from "./ImageSlots";
import {
  fieldForSlot,
  firstImage,
  mediaFilename,
  slotForPaste,
  suffixForSlot,
} from "./mediaSlots";
import { applyNoteSlotMove, noteDragPayload } from "./noteSlotDrag";
import { shouldSeedDraft } from "./notePanel";
import { useShiftHeld } from "./useShiftHeld";

/**
 * The added card, expanded in place.
 *
 * PRD §6: "Card creation is a single motion… Adding a card doesn't mean landing in a separate
 * editor and fixing things up." So the result you were looking at BECOMES the finished card, in the
 * same position on screen — no new tab, no new page, no navigation.
 *
 * Audio is always fetched; there is no toggle. The `Fetch audio when adding` and
 * `Auto-fill from captures` checkboxes are both gone.
 *
 * The same panel serves `E` on a card you already own. Editing a word you carded last month is the
 * same motion as fixing one you carded ten seconds ago, and it should not open a different UI — but
 * `Undo add` is withheld, because there is no add to undo and deleting an old card by reflex is not
 * a mistake worth making easy.
 */
interface AddedPanelProps {
  /**
   * The headword the panel is titled with, and the ONLY thing it uses the row for (日本-498).
   *
   * A string rather than the `Definition` it used to take, because since grammar rows can be
   * edited too the row behind this panel is not always a dictionary entry — a 文法 row's headword
   * is its DoJG concept. Nothing else here ever read the entry, so narrowing the prop to what is
   * actually used is what let the panel serve both kinds unchanged.
   */
  term: string;
  noteId: number;
  /**
   * Changes whenever the server has rewritten this note behind the panel's back.
   *
   * Reloading on `noteId` alone is not enough, because an APPEND keeps the note it lands on: press
   * add on an archaic ねこま row while the 猫 card is open and the collection gains a section the
   * panel never shows. Two separate things then have to happen — the note is re-fetched, and the
   * draft is re-seeded from it — because the fields render from the draft, which is otherwise
   * seeded exactly once on purpose.
   */
  revision: number;
  /** False when reached with `E` — the badge changes and Undo add is withheld. */
  justAdded: boolean;
  /** The sentence the card was mined from, whose images it inherited. */
  inheritedImage: string | null;
  onCollapse: () => void;
  onUndo?: () => void;
}

/**
 * What the badge says about audio, read off the note rather than off the add response.
 *
 * Three outcomes, and only one of them used to be visible. A fetch that *fails* deliberately tags
 * nothing so the pipeline's audio pass retries it — which meant a broken fetch was silent, and
 * audio being broken for the entire Python cutover went unnoticed on exactly this badge.
 *
 * Read off the note because that is the durable answer: a message in the dock is erased ~80ms later
 * by the re-lookup that `noteMined` triggers, and this has to still be here when you look at it.
 */
function audioSuffix(note: NoteDetail | null): string {
  if (note === null) return "";
  if (note.audio.filename) return " · 音声取得済";
  // The service answered and definitively has no recording for this word. Nothing to retry.
  if (note.tags.includes("NoAudio")) return " · 録音なし";
  // Its reading could not be sent — non-Japanese characters. Also terminal.
  if (note.tags.includes("BadReading")) return " · 音声なし";
  // Untagged and empty: the fetch itself failed. The pipeline's audio pass will retry it.
  return " · 音声失敗";
}

export function AddedPanel({
  term,
  noteId,
  revision,
  justAdded,
  inheritedImage,
  onCollapse,
  onUndo,
}: AddedPanelProps) {
  const [note, setNote] = useState<NoteDetail | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busySlot, setBusySlot] = useState<SlotKey | null>(null);
  const [tagBusy, setTagBusy] = useState(false);
  const shiftHeld = useShiftHeld();

  /**
   * Bumped by every media write, so the initial load cannot land on top of one.
   *
   * The panel opens and immediately fetches the note; a paste in that window attaches an image and
   * sets the fresh note, and then the in-flight `getNote` resolved with the PRE-attach snapshot and
   * the thumbnail you had just pasted vanished. The click and drop paths cannot hit this — they are
   * gated on `note` existing — but paste is a window listener, and pasting the moment the panel
   * appears is exactly the gesture the design is built around.
   */
  const writeGen = useRef(0);
  /** The draft is seeded once, by whichever of the load or a media write arrives first. */
  const seeded = useRef(false);
  /** The `revision` the draft currently reflects, so only a CHANGE re-seeds it. */
  const seededRevision = useRef(revision);

  /** `applyNote` is stable, so it cannot read `dirty` as state without going stale. */
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;

  const applyNote = useCallback((loaded: NoteDetail, rewrittenByServer = false) => {
    setNote(loaded);
    // The three interacting rules live in `shouldSeedDraft`, where they are pinned by tests.
    if (
      !shouldSeedDraft({
        seeded: seeded.current,
        rewrittenByServer,
        dirty: dirtyRef.current,
      })
    ) {
      return;
    }
    seeded.current = true;
    setDraft(noteDraft(loaded));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const gen = writeGen.current;
    // A revision bump means the note was rewritten server-side while this panel was open, so the
    // draft is re-seeded from what came back. The first load has nothing to re-seed over.
    const reseed = revision !== seededRevision.current;
    seededRevision.current = revision;
    void getNote(noteId)
      .then((loaded) => {
        if (cancelled || gen !== writeGen.current) return;
        applyNote(loaded, reseed);
      })
      .catch(() => {
        // `カードを読み込み中…` forever reads as a hang; naming the failure lets you retry.
        if (!cancelled) setError("カードを読み込めませんでした。同期ハブは動いていますか？");
      });
    return () => {
      cancelled = true;
    };
  }, [noteId, revision, applyNote]);

  /** True when the edit is safely stored (or there was nothing to store). */
  const save = async (): Promise<boolean> => {
    if (!note || !dirty) return true;
    setSaving(true);
    setError(null);
    try {
      await updateNote(note.noteId, noteUpdateBody(draft, note));
      setDirty(false);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : "保存に失敗しました。");
      return false;
    } finally {
      setSaving(false);
    }
  };

  /**
   * Tags are written on the spot, like the media slots below and unlike the text fields.
   *
   * They are read off `note` rather than `draft`, so folding them into `save()` would mean a
   * tag-only change had to fake a dirty draft to persist at all. `writeGen` is bumped and the
   * response fed through `applyNote` for the same reason `attach` does it: a `getNote` already in
   * flight must not land on top of this, and `applyNote` refreshes the note without disturbing an
   * unsaved text draft (`shouldSeedDraft`).
   *
   * **It does not announce the change itself, and that is the point of 日本-283.** `updateNote`
   * carries this tab's `X-Client-Id`, so the server publishes `collectionChanged` and every OTHER
   * tab refetches while this one ignores its own frame — because the line above has already
   * applied the result locally. Calling `notifyCollectionChanged()` here would defeat exactly that
   * filter and make the write round-trip into a refetch fighting the update already on screen.
   */
  const writeTags = async (next: string[]) => {
    if (!note) return;
    setTagBusy(true);
    setError(null);
    try {
      const updated = await updateNote(note.noteId, { tags: next });
      writeGen.current += 1;
      applyNote(updated);
      forgetSuggestedTags();
    } catch (e) {
      setError(e instanceof Error ? e.message : "タグを変更できませんでした。");
    } finally {
      setTagBusy(false);
    }
  };

  /**
   * Put a file on one of the card's own image fields.
   *
   * One request, unlike the reading log's store-then-quote: a sentence is not a note and may never
   * become one, but a card already exists, so the bytes and the field move together inside a single
   * collection write. The response IS the updated note, so nothing needs re-fetching.
   */
  const attach = async (which: SlotKey, file: File) => {
    setBusySlot(which);
    setError(null);
    try {
      const updated = await attachMedia(
        noteId,
        fieldForSlot(which),
        file,
        mediaFilename(file.type, Date.now(), suffixForSlot(which)),
      );
      writeGen.current += 1;
      applyNote(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : "画像を添付できませんでした。");
    } finally {
      setBusySlot(null);
    }
  };

  /**
   * Move an image between this card's two slots.
   *
   * Unconfirmed, like `attach`: a drag is deliberate, and both outcomes are reversible by dragging
   * back. `busySlot` covers the whole move rather than each request, because a swap's two writes
   * are one gesture and half a swap is not a state worth painting.
   */
  const dropSlot = async (which: SlotKey, payload: string) => {
    if (!note) return;
    setBusySlot(which);
    setError(null);
    try {
      const updated = await applyNoteSlotMove(note, which, payload);
      if (updated) {
        writeGen.current += 1;
        applyNote(updated);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "画像を移動できませんでした。");
    } finally {
      setBusySlot(null);
    }
  };

  const clear = async (which: SlotKey) => {
    // Unconfirmed, matching the reading log's × and unlike the Cards dock's. You attached this
    // seconds ago, it is on screen, and the file itself survives in the media store either way.
    setBusySlot(which);
    setError(null);
    try {
      const updated = await clearMedia(noteId, fieldForSlot(which));
      writeGen.current += 1;
      applyNote(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : "画像を消去できませんでした。");
    } finally {
      setBusySlot(null);
    }
  };

  /**
   * Ctrl+V lands on the CARD while this panel is open.
   *
   * `Read` owns an identical window listener aimed at the active *sentence*, and yields to this one
   * by checking that nothing is expanded — registration order makes `defaultPrevented` useless here,
   * since `Read` mounts first and would always run first. Exactly one of the two ever acts, and the
   * invariant is commented on both sides.
   *
   * The panel is what you are working on, and it is open because you just carded from that sentence,
   * so the card is the right target. The sentence's own slots stay reachable by drop and by click.
   */
  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      // A browser copy carries text/html AND an image — the very case `firstImage` exists for.
      // Without this, pasting a definition into the English field would swallow the text and hang
      // the page's illustration on the card instead. The English field is exactly where you would
      // paste a definition, and this panel is open precisely when you have just carded something.
      if (isTextEntry(event.target)) return;
      const file = firstImage(event.clipboardData?.files);
      if (!file) return;
      event.preventDefault();
      void attach(slotForPaste(shiftHeld.current), file);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  });

  // A failed save must NOT collapse: the panel closing while the edit is lost reads as saved.
  const saveAndCollapse = () =>
    void save().then((ok) => {
      if (ok) onCollapse();
    });

  // Ctrl+Enter saves and keeps reading; Escape collapses. It saves on collapse either way, so
  // Escape is never a way to lose an edit.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // Another control already answered this key (the sentence edit's own Escape) — leave it be.
      if (event.defaultPrevented) return;
      if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        saveAndCollapse();
      } else if (event.key === "Escape") {
        event.preventDefault();
        saveAndCollapse();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <div className="mr-3 flex flex-col gap-[10px] rounded-[var(--r-3)] border border-line-strong bg-panel px-4 py-[13px] shadow-[var(--sh-frame)]">
      <div className="flex flex-wrap items-baseline gap-[10px]">
        <span className="font-jp text-[27px]/[1.1] font-semibold">{term}</span>
        <span className="rounded-[var(--r-2)] border border-line bg-line-soft px-[7px] py-[3px] text-[14px]/[1.3] font-semibold text-ink-3">
          {justAdded ? `追加済 · 補完済${audioSuffix(note)}` : "収録済 · 編集中"}
        </span>
        <div className="flex-1" />
        <span className="font-mono text-meta text-ink-4">Ctrl+Enterで保存 · Escで閉じる</span>
      </div>

      {/* The card's own images, and they are editable here.
          They were read-only thumbnails, which meant a card mined from a sentence you had not
          screenshotted could never get a picture — the sentence's slots are the only way in, and
          the sentence is gone by then. The endpoint for this existed and had no caller.

          `inheritedImage` is only a placeholder for the moment before the note loads: once `note`
          is here it is authoritative, and falling back to the inherited name would resurrect an
          image you just cleared. */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Only on a fresh add. Reached with `E`, `inheritedImage` is the card's OWN image —
            `editCarded` passes `note.image.filename` — so the badge would claim a capture from
            this session was the source of a picture you attached months ago. */}
        {justAdded && inheritedImage && (
          <span className="rounded-[var(--r-2)] border border-line bg-line-soft px-[7px] py-[3px] text-[14px]/[1.4] font-semibold text-ink-3">
            この文から
          </span>
        )}
        <ImageSlots
          image={{
            value: note?.image.raw ?? "",
            filename: note ? note.image.filename : inheritedImage,
          }}
          extraImage={{
            value: note?.extraImage.raw ?? "",
            filename: note?.extraImage.filename ?? null,
          }}
          // Full size, not `compact`: compact hides both the hint and the × , and a slot you
          // cannot clear is not an editor.
          onFile={note ? (which, file) => void attach(which, file) : undefined}
          onClear={note ? (which) => void clear(which) : undefined}
          onSlotDragStart={(which) => noteDragPayload(note, which)}
          onSlotDrop={note ? (which, payload) => void dropSlot(which, payload) : undefined}
          busy={busySlot}
        />
      </div>

      {error && <p className="text-meta text-[color:var(--ink-3)]">{error}</p>}

      {note === null ? (
        !error && <p className="text-secondary text-ink-3">カードを読み込み中…</p>
      ) : (
        <div className="flex flex-col gap-2">
          {noteFieldsFor(note).map(({ key, label, multiline }) => (
            <NoteFieldRow
              key={String(key)}
              label={label}
              multiline={multiline}
              value={draft[key as string] ?? ""}
              onChange={(next) => {
                setDraft((d) => ({ ...d, [key as string]: next }));
                setDirty(true);
              }}
            />
          ))}

          <TagChips tags={note.tags} busy={tagBusy} onChange={(next) => void writeTags(next)} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-[9px] border-t border-line-soft pt-[11px]">
        <button
          type="button"
          onClick={saveAndCollapse}
          disabled={saving}
          className="rounded-[var(--r-2)] bg-ink px-[13px] py-2 text-[14px]/[1] font-semibold text-void disabled:opacity-60"
        >
          {saving ? "保存中…" : "保存して読み続ける"}
          <span className="ml-[7px] font-mono text-[14px]/[1] opacity-60">Ctrl+Enter</span>
        </button>
        {justAdded && onUndo && (
          <button
            type="button"
            onClick={onUndo}
            className="rounded-[var(--r-2)] border border-accent-line bg-accent-tint px-[13px] py-2 text-[14px]/[1] font-semibold text-accent-hi"
          >
            追加を取り消し
            <span className="ml-[7px] font-mono text-[14px]/[1] opacity-60">Ctrl+Z</span>
          </button>
        )}
      </div>
    </div>
  );
}
